import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Tag,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Badge,
  Select,
  Alert,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { maintenanceOfferService, MaintenanceOffer, CreateMaintenanceOfferDto, PaidOption } from '../services/maintenanceOfferService';
import { subscriptionsService, Subscription } from '../services/subscriptionsService';
import { installationServiceService, InstallationService, CreateInstallationServiceDto } from '../services/installationServiceService';
import { repairServiceService, RepairService, CreateRepairServiceDto } from '../services/repairServiceService';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;

const offerTypeLabels = {
  on_demand: 'À la demande',
  annual: 'Abonnement annuel'
} as const;

const equipmentCategoryLabels = {
  all: 'Toutes catégories',
  mural: 'Climatiseur mural',
  cassette: 'Cassette',
  allege_armoire: 'Allège / armoire',
  gainable: 'Gainable'
} as const;

const pricingModeLabels = {
  per_equipment: 'Par équipement',
  flat: 'Forfait global'
} as const;

// Couvre tout l'ENUM `subscriptions.status` de l'API, y compris les états
// propres aux contrats annuels (attente de paiement, attente du solde).
const subscriptionStatusMeta: Record<string, { label: string; color: string }> = {
  pending_payment: { label: 'Attente paiement', color: 'gold' },
  active: { label: 'Active', color: 'green' },
  awaiting_second_payment: { label: 'Attente solde 50 %', color: 'volcano' },
  completed: { label: 'Terminée', color: 'blue' },
  used: { label: 'Consommée', color: 'geekblue' },
  expired: { label: 'Expirée', color: 'red' },
  cancelled: { label: 'Annulée', color: 'red' }
};

const subscriptionPaymentMeta: Record<string, { label: string; color: string }> = {
  pending: { label: 'En attente', color: 'orange' },
  partial: { label: 'Acompte 50 % versé', color: 'gold' },
  paid: { label: 'Payé', color: 'green' },
  failed: { label: 'Échoué', color: 'red' }
};

const MaintenanceOffersPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';

  const [offers, setOffers] = useState<MaintenanceOffer[]>([]);
  const [offerSearchText, setOfferSearchText] = useState('');
  const [offerTypeFilter, setOfferTypeFilter] = useState<string>('all');
  const [offerCategoryFilter, setOfferCategoryFilter] = useState<string>('all');
  const [offerStatusFilter, setOfferStatusFilter] = useState<string>('all');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [installationServices, setInstallationServices] = useState<InstallationService[]>([]);
  const [repairServices, setRepairServices] = useState<RepairService[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [loadingInstallation, setLoadingInstallation] = useState(false);
  const [loadingRepair, setLoadingRepair] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [installationModalVisible, setInstallationModalVisible] = useState(false);
  const [repairModalVisible, setRepairModalVisible] = useState(false);
  const [editingOffer, setEditingOffer] = useState<MaintenanceOffer | null>(null);
  const [editingInstallation, setEditingInstallation] = useState<InstallationService | null>(null);
  const [editingRepair, setEditingRepair] = useState<RepairService | null>(null);
  const [form] = Form.useForm();
  const [installationForm] = Form.useForm();
  const [repairForm] = Form.useForm();
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState('');
  const [paidOptions, setPaidOptions] = useState<PaidOption[]>([]);
  const [customPaidOption, setCustomPaidOption] = useState('');
  const [activeTab, setActiveTab] = useState('offers');
  const watchedOfferType = Form.useWatch('offer_type', form);
  const watchedPrice = Form.useWatch('price', form) || 0;
  const watchedDiscount = Form.useWatch('annual_discount_percentage', form) || 0;

  useEffect(() => {
    loadOffers();
    loadSubscriptions();
    loadInstallationServices();
    loadRepairServices();
  }, []);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const data = await maintenanceOfferService.getAll();
      setOffers(data);
    } catch (error) {
      message.error('Erreur lors du chargement des offres');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    setLoadingSubscriptions(true);
    try {
      const data = await subscriptionsService.getAll();
      setSubscriptions(data);
    } catch (error) {
      message.error('Erreur lors du chargement des souscriptions');
      console.error(error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const loadInstallationServices = async () => {
    setLoadingInstallation(true);
    try {
      const data = await installationServiceService.getAll();
      setInstallationServices(data);
    } catch (error) {
      message.error('Erreur lors du chargement des services d\'installation');
      console.error(error);
    } finally {
      setLoadingInstallation(false);
    }
  };

  const loadRepairServices = async () => {
    setLoadingRepair(true);
    try {
      const data = await repairServiceService.getAll();
      setRepairServices(data);
    } catch (error) {
      message.error('Erreur lors du chargement des services de réparation');
      console.error(error);
    } finally {
      setLoadingRepair(false);
    }
  };

  const handleCreate = () => {
    setEditingOffer(null);
    setFeatures([]);
    setPaidOptions([]);
    setCustomPaidOption('');
    form.resetFields();
    form.setFieldsValue({
      offer_type: 'on_demand',
      equipment_category: 'all',
      pricing_mode: 'per_equipment',
      duration_months: 12,
      visits_total: 4,
      visit_interval_months: 3,
      annual_discount_percentage: 5,
      includes_freon: false,
      isActive: true
    });
    setModalVisible(true);
  };

  const handleEdit = (offer: MaintenanceOffer) => {
    setEditingOffer(offer);
    setFeatures(offer.features || []);
    
    const rawPaidOptions = offer.paidOptions || (offer as any).paid_options || [];
    let parsedPaidOptions: PaidOption[] = [];
    if (typeof rawPaidOptions === 'string') {
      try {
        parsedPaidOptions = JSON.parse(rawPaidOptions);
      } catch {
        parsedPaidOptions = [];
      }
    } else if (Array.isArray(rawPaidOptions)) {
      parsedPaidOptions = rawPaidOptions;
    }
    setPaidOptions(parsedPaidOptions);
    setCustomPaidOption('');
    form.setFieldsValue({
      title: offer.title,
      description: offer.description,
      price: offer.price,
      priceNote: offer.priceNote || offer.price_note || '',
      offer_type: offer.offer_type || 'on_demand',
      equipment_category: offer.equipment_category || 'all',
      pricing_mode: offer.pricing_mode || 'per_equipment',
      duration_months: offer.duration_months || offer.duration || 12,
      visits_total: offer.visits_total || 4,
      visit_interval_months: offer.visit_interval_months || 3,
      annual_discount_percentage: offer.annual_discount_percentage ?? 5,
      includes_freon: offer.includes_freon ?? false,
      isActive: offer.isActive
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await maintenanceOfferService.delete(id);
      message.success('Offre supprimée avec succès');
      loadOffers();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      await maintenanceOfferService.toggleActive(id, !isActive);
      message.success(`Offre ${!isActive ? 'activée' : 'désactivée'} avec succès`);
      loadOffers();
    } catch (error) {
      message.error('Erreur lors de la modification');
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const isAnnual = values.offer_type === 'annual';
      const data: CreateMaintenanceOfferDto = {
        title: values.title.trim(),
        description: values.description ? values.description.trim() : '',
        price: values.price,
        priceNote: values.priceNote?.trim() || undefined,
        offer_type: values.offer_type,
        equipment_category: values.equipment_category,
        pricing_mode: values.pricing_mode,
        duration_months: isAnnual ? (values.duration_months || 12) : 1,
        visits_total: isAnnual ? (values.visits_total || 4) : 1,
        visit_interval_months: isAnnual ? (values.visit_interval_months || 3) : 1,
        annual_discount_percentage: isAnnual ? (values.annual_discount_percentage ?? 0) : 0,
        includes_freon: !!values.includes_freon,
        features: Array.from(new Set(features.map(feature => feature.trim()).filter(Boolean))),
        paidOptions,
        isActive: values.isActive !== undefined ? values.isActive : true
      };

      if (editingOffer) {
        await maintenanceOfferService.update(editingOffer.id, data);
        message.success('Offre modifiée avec succès');
      } else {
        await maintenanceOfferService.create(data);
        message.success('Offre créée avec succès');
      }

      setModalVisible(false);
      loadOffers();
    } catch (error) {
      message.error('Erreur lors de l\'enregistrement');
      console.error(error);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      const items = newFeature
        .split(/[,;\n]/)
        .map(item => item.trim())
        .filter(item => item.length > 0);
      if (items.length > 0) {
        setFeatures(prev => [...prev, ...items]);
        setNewFeature('');
      }
    }
  };

  const removeFeature = (index: number) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handleCancelSubscription = async (id: number) => {
    try {
      await subscriptionsService.cancel(id);
      message.success('Souscription annulée avec succès');
      loadSubscriptions();
    } catch (error) {
      message.error('Erreur lors de l\'annulation');
      console.error(error);
    }
  };

  const handleUpdatePaymentStatus = async (id: number, status: 'pending' | 'paid' | 'failed') => {
    try {
      await subscriptionsService.updatePaymentStatus(id, status);
      message.success('Statut de paiement mis à jour');
      loadSubscriptions();
    } catch (error) {
      message.error('Erreur lors de la mise à jour');
      console.error(error);
    }
  };

  const handleDeleteSubscription = async (id: number) => {
    try {
      await subscriptionsService.delete(id);
      message.success('Souscription supprimée avec succès');
      loadSubscriptions();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const handleRenewSubscription = async (id: number) => {
    try {
      await subscriptionsService.renew(id);
      message.success('Souscription renouvelée avec succès');
      loadSubscriptions();
    } catch (error) {
      message.error('Erreur lors du renouvellement');
      console.error(error);
    }
  };

  // Installation Services Handlers
  const handleCreateInstallation = () => {
    setEditingInstallation(null);
    installationForm.resetFields();
    setInstallationModalVisible(true);
  };

  const handleEditInstallation = (service: InstallationService) => {
    setEditingInstallation(service);
    installationForm.setFieldsValue({
      title: service.title,
      model: service.model,
      description: service.description,
      availabilityInfo: service.availabilityInfo,
      isActive: service.isActive
    });
    setInstallationModalVisible(true);
  };

  const handleDeleteInstallation = async (id: number) => {
    try {
      await installationServiceService.delete(id);
      message.success('Service supprimé avec succès');
      loadInstallationServices();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const handleToggleInstallation = async (id: number) => {
    try {
      await installationServiceService.toggleActive(id);
      message.success('Statut modifié avec succès');
      loadInstallationServices();
    } catch (error) {
      message.error('Erreur lors de la modification');
      console.error(error);
    }
  };

  const handleSubmitInstallation = async () => {
    try {
      const values = await installationForm.validateFields();
      const data: CreateInstallationServiceDto = values;

      if (editingInstallation) {
        await installationServiceService.update(editingInstallation.id, data);
        message.success('Service modifié avec succès');
      } else {
        await installationServiceService.create(data);
        message.success('Service créé avec succès');
      }

      setInstallationModalVisible(false);
      loadInstallationServices();
    } catch (error) {
      message.error('Erreur lors de l\'enregistrement');
      console.error(error);
    }
  };

  // Repair Services Handlers
  const handleCreateRepair = () => {
    setEditingRepair(null);
    repairForm.resetFields();
    setRepairModalVisible(true);
  };

  const handleEditRepair = (service: RepairService) => {
    setEditingRepair(service);
    repairForm.setFieldsValue({
      title: service.title,
      model: service.model,
      description: service.description,
      isActive: service.isActive
    });
    setRepairModalVisible(true);
  };

  const handleDeleteRepair = async (id: number) => {
    try {
      await repairServiceService.delete(id);
      message.success('Service supprimé avec succès');
      loadRepairServices();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const handleToggleRepair = async (id: number) => {
    try {
      await repairServiceService.toggleActive(id);
      message.success('Statut modifié avec succès');
      loadRepairServices();
    } catch (error) {
      message.error('Erreur lors de la modification');
      console.error(error);
    }
  };

  const handleSubmitRepair = async () => {
    try {
      const values = await repairForm.validateFields();
      const data: CreateRepairServiceDto = values;

      if (editingRepair) {
        await repairServiceService.update(editingRepair.id, data);
        message.success('Service modifié avec succès');
      } else {
        await repairServiceService.create(data);
        message.success('Service créé avec succès');
      }

      setRepairModalVisible(false);
      loadRepairServices();
    } catch (error) {
      message.error('Erreur lors de l\'enregistrement');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: MaintenanceOffer) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
        </div>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Mode',
      dataIndex: 'offer_type',
      key: 'offer_type',
      render: (type: MaintenanceOffer['offer_type'] = 'on_demand') => (
        <Tag color={type === 'annual' ? 'purple' : 'blue'}>{offerTypeLabels[type]}</Tag>
      ),
      filters: [
        { text: offerTypeLabels.on_demand, value: 'on_demand' },
        { text: offerTypeLabels.annual, value: 'annual' }
      ],
      onFilter: (value: any, record: MaintenanceOffer) => (record.offer_type || 'on_demand') === value
    },
    {
      title: 'Catégorie',
      dataIndex: 'equipment_category',
      key: 'equipment_category',
      render: (category: MaintenanceOffer['equipment_category'] = 'all') => equipmentCategoryLabels[category],
      filters: Object.entries(equipmentCategoryLabels).map(([value, text]) => ({ text, value })),
      onFilter: (value: any, record: MaintenanceOffer) => (record.equipment_category || 'all') === value
    },
    {
      title: 'Prix',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: MaintenanceOffer) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{Number(price).toLocaleString('fr-FR')} FCFA</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {pricingModeLabels[record.pricing_mode || 'per_equipment']}
          </div>
          {(record.priceNote || record.price_note) && (
            <div style={{ fontSize: '13px', color: '#1890ff', fontWeight: 500 }}>{record.priceNote || record.price_note}</div>
          )}
        </div>
      ),
      sorter: (a: MaintenanceOffer, b: MaintenanceOffer) => a.price - b.price
    },
    {
      title: 'Cadence / inclus',
      key: 'cadence',
      render: (_: any, record: MaintenanceOffer) => (
        <Space direction="vertical" size={2}>
          {record.offer_type === 'annual' ? (
            <>
              <span>{record.visits_total || 0} visite(s) / {record.duration_months || record.duration || 12} mois</span>
              <span style={{ color: '#666', fontSize: 12 }}>Tous les {record.visit_interval_months || 0} mois · remise {record.annual_discount_percentage || 0}%</span>
            </>
          ) : <span>Intervention ponctuelle</span>}
          {record.includes_freon && <Tag color="cyan">Fréon inclus</Tag>}
        </Space>
      )
    },
    {
      title: 'Inclus / Options',
      key: 'features_options',
      render: (_: any, record: MaintenanceOffer) => {
        const paidOpts = record.paidOptions || (record as any).paid_options || [];
        const features = record.features || [];
        return (
          <div>
            {features && features.length > 0 && (
              <Tag color="blue">{features.length} fonctionnalités</Tag>
            )}
            {paidOpts && paidOpts.length > 0 && (
              <Tag color="orange" style={{ marginTop: 2 }}>
                ⚠️ {paidOpts.length} option{paidOpts.length > 1 ? 's' : ''} payante{paidOpts.length > 1 ? 's' : ''}
              </Tag>
            )}
            {(!features || features.length === 0) && (!paidOpts || paidOpts.length === 0) && (
              <Tag>Aucune</Tag>
            )}
          </div>
        );
      }
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      onFilter: (value: any, record: MaintenanceOffer) => record.isActive === value
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: MaintenanceOffer) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            style={{ padding: 0 }}
          >
            Modifier
          </Button>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette offre ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small" style={{ padding: 0 }}>
              Supprimer
            </Button>
          </Popconfirm>
          <Button
            type="link"
            onClick={() => handleToggleActive(record.id, record.isActive)}
            size="small"
            style={{ padding: 0 }}
          >
            {record.isActive ? 'Désactiver' : 'Activer'}
          </Button>
        </Space>
      )
    }
  ];

  const activeOffers = offers.filter(o => o.isActive);

  const filteredOffers = useMemo(() => {
    return offers.filter(offer => {
      // 1. Recherche textuelle
      if (offerSearchText.trim()) {
        const query = offerSearchText.trim().toLowerCase();
        const title = (offer.title || '').toLowerCase();
        const desc = (offer.description || '').toLowerCase();
        const priceNote = (offer.priceNote || (offer as any).price_note || '').toLowerCase();
        const categoryLabel = (equipmentCategoryLabels[offer.equipment_category || 'all'] || '').toLowerCase();
        const modeLabel = (offerTypeLabels[offer.offer_type || 'on_demand'] || '').toLowerCase();
        const features = (offer.features || []).join(' ').toLowerCase();

        const match = title.includes(query) ||
          desc.includes(query) ||
          priceNote.includes(query) ||
          categoryLabel.includes(query) ||
          modeLabel.includes(query) ||
          features.includes(query);

        if (!match) return false;
      }

      // 2. Filtre Mode
      if (offerTypeFilter && offerTypeFilter !== 'all') {
        const type = offer.offer_type || 'on_demand';
        if (type !== offerTypeFilter) return false;
      }

      // 3. Filtre Catégorie
      if (offerCategoryFilter && offerCategoryFilter !== 'all') {
        const cat = offer.equipment_category || 'all';
        if (offerCategoryFilter === 'only_all') {
          if (cat !== 'all') return false;
        } else {
          if (cat !== offerCategoryFilter) return false;
        }
      }

      // 4. Filtre Statut
      if (offerStatusFilter && offerStatusFilter !== 'all') {
        const isActive = offerStatusFilter === 'active';
        if (offer.isActive !== isActive) return false;
      }

      return true;
    });
  }, [offers, offerSearchText, offerTypeFilter, offerCategoryFilter, offerStatusFilter]);

  const hasActiveOfferFilters = Boolean(
    offerSearchText.trim() ||
    (offerTypeFilter && offerTypeFilter !== 'all') ||
    (offerCategoryFilter && offerCategoryFilter !== 'all') ||
    (offerStatusFilter && offerStatusFilter !== 'all')
  );

  const handleResetOfferFilters = () => {
    setOfferSearchText('');
    setOfferTypeFilter('all');
    setOfferCategoryFilter('all');
    setOfferStatusFilter('all');
  };
  // Un contrat annuel en attente du solde reste un contrat en cours de vie.
  const activeSubscriptions = subscriptions.filter(
    s => s.status === 'active' || s.status === 'awaiting_second_payment'
  );
  // Solde de 50 % encore dû sur un contrat actif en cours.
  const pendingPayments = subscriptions.filter(
    s => Number(s.price) > 0
      && (s.payment_status === 'partial' || s.status === 'awaiting_second_payment')
      && s.status !== 'cancelled'
  ).length;
  // Encaissé réel : total des contrats soldés + acompte des contrats partiels.
  const subscriptionRevenue = subscriptions.reduce((sum, s) => {
    if (s.payment_status === 'paid') return sum + Number(s.price || 0);
    if (s.payment_status === 'partial') {
      return sum + Number(s.first_payment_amount ?? Math.ceil(Number(s.price || 0) / 2));
    }
    return sum;
  }, 0);

  const subscriptionColumns = [
    {
      title: 'Client',
      key: 'customer',
      render: (_: any, record: Subscription) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.customer?.first_name} {record.customer?.last_name}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {record.customer?.email}
          </div>
        </div>
      )
    },
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: (_: any, record: Subscription) => {
        if (record.maintenance_offer_id) return <Tag color="blue">Maintenance</Tag>;
        if (record.installation_service_id) return <Tag color="green">Installation</Tag>;
        if (record.repair_service_id) return <Tag color="orange">Réparation</Tag>;
        return <Tag>Inconnu</Tag>;
      },
      filters: [
        { text: 'Maintenance', value: 'maintenance' },
        { text: 'Installation', value: 'installation' },
        { text: 'Réparation', value: 'repair' }
      ],
      onFilter: (value: any, record: Subscription) => {
        if (value === 'maintenance') return !!record.maintenance_offer_id;
        if (value === 'installation') return !!record.installation_service_id;
        if (value === 'repair') return !!record.repair_service_id;
        return false;
      }
    },
    {
      title: 'Offre / Service',
      key: 'offer',
      render: (_: any, record: Subscription) => {
        // Offre maintenance
        if (record.offer) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{record.offer.title}</div>
            </div>
          );
        }
        // Service d'installation
        if (record.installationService) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{record.installationService.title}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                {record.installationService.model}
              </div>
            </div>
          );
        }
        // Service de réparation
        if (record.repairService) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{record.repairService.title}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                {record.repairService.model}
              </div>
            </div>
          );
        }
        return <span style={{ color: '#888' }}>Non spécifié</span>;
      }
    },
    {
      title: 'Montant',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${price.toLocaleString('fr-FR')} FCFA`,
      sorter: (a: Subscription, b: Subscription) => a.price - b.price
    },
    {
      title: 'Période',
      key: 'period',
      render: (_: any, record: Subscription) => (
        <div style={{ fontSize: '12px' }}>
          <div>📅 Début: {new Date(record.start_date).toLocaleDateString('fr-FR')}</div>
          <div>📅 Fin: {new Date(record.end_date).toLocaleDateString('fr-FR')}</div>
        </div>
      )
    },
    {
      title: 'Contrat',
      key: 'contract',
      render: (_: any, record: Subscription) => {
        const snapshot = record.offer_snapshot;
        const isAnnual = record.contract_type === 'scheduled'
          || snapshot?.offer_type === 'annual'
          || record.offer?.offer_type === 'annual';
        if (!isAnnual) {
          return <Tag color="blue">{offerTypeLabels.on_demand}</Tag>;
        }
        const visitsTotal = record.visits_total ?? snapshot?.visits_total ?? 0;
        const interval = record.visit_interval_months ?? snapshot?.visit_interval_months;
        const discount = snapshot?.annual_discount_percentage ?? record.offer?.annual_discount_percentage ?? 0;
        return (
          <Space direction="vertical" size={2}>
            <Tag color="purple">{offerTypeLabels.annual}</Tag>
            <span style={{ fontSize: 12 }}>
              {record.visits_completed ?? 0}/{visitsTotal} visite(s)
              {interval ? ` · tous les ${interval} mois` : ''}
            </span>
            {record.next_visit_date && (
              <span style={{ fontSize: 12, color: '#666' }}>
                Prochaine : {new Date(record.next_visit_date).toLocaleDateString('fr-FR')}
              </span>
            )}
            {discount > 0 && (
              <span style={{ fontSize: 12, color: '#389e0d' }}>Remise annuelle −{discount}%</span>
            )}
            {(snapshot?.includes_freon ?? record.offer?.includes_freon) && (
              <Tag color="cyan">Fréon inclus</Tag>
            )}
          </Space>
        );
      },
      filters: [
        { text: offerTypeLabels.annual, value: 'scheduled' },
        { text: offerTypeLabels.on_demand, value: 'on_demand' }
      ],
      onFilter: (value: any, record: Subscription) =>
        (record.contract_type || 'on_demand') === value
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={subscriptionStatusMeta[status]?.color}>
          {subscriptionStatusMeta[status]?.label || status}
        </Tag>
      ),
      filters: Object.entries(subscriptionStatusMeta).map(([value, meta]) => ({
        text: meta.label,
        value
      })),
      onFilter: (value: any, record: Subscription) => record.status === value
    },
    {
      title: 'Paiement',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status: string, record: Subscription) => {
        const isFree = Number(record.price) === 0;
        if (isFree) {
          return <Tag color="blue">Gratuit</Tag>;
        }
        const meta = subscriptionPaymentMeta[status];
        return (
          <Space direction="vertical" size={2}>
            <Tag color={meta?.color}>{meta?.label || status}</Tag>
            {status === 'partial' && Number(record.second_payment_amount) > 0 && (
              <span style={{ fontSize: 12, color: '#666' }}>
                Solde : {Number(record.second_payment_amount).toLocaleString('fr-FR')} FCFA
              </span>
            )}
          </Space>
        );
      },
      filters: Object.entries(subscriptionPaymentMeta).map(([value, meta]) => ({
        text: meta.label,
        value
      })),
      onFilter: (value: any, record: Subscription) => record.payment_status === value
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: Subscription) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {/* Annuler - Seulement pour les souscriptions actives */}
          {record.status === 'active' && (
            <Popconfirm
              title="Annuler cette souscription ?"
              onConfirm={() => handleCancelSubscription(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button type="link" danger size="small" style={{ padding: 0 }}>
                🚫 Annuler
              </Button>
            </Popconfirm>
          )}
          
          {/* Marquer payé - Seulement pour paiements en attente */}
          {record.payment_status === 'pending' && record.status === 'active' && (
            <Button
              type="link"
              onClick={() => handleUpdatePaymentStatus(record.id, 'paid')}
              size="small"
              style={{ padding: 0, color: 'green' }}
            >
              ✅ Marquer payé
            </Button>
          )}
          
          {/* Renouveler - Pour expirées ou annulées */}
          {(record.status === 'expired' || record.status === 'cancelled') && (
            <Popconfirm
              title="Renouveler cette souscription ?"
              description="Une nouvelle souscription sera créée avec la même offre."
              onConfirm={() => handleRenewSubscription(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button type="link" size="small" style={{ padding: 0, color: '#1890ff' }}>
                🔄 Renouveler
              </Button>
            </Popconfirm>
          )}
          
          {/* Supprimer - Pour échecs de paiement ou annulées */}
          {(record.payment_status === 'failed' || record.status === 'cancelled') && (
            <Popconfirm
              title="Supprimer cette souscription ?"
              description="Cette action est irréversible."
              onConfirm={() => handleDeleteSubscription(record.id)}
              okText="Oui"
              cancelText="Non"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger size="small" style={{ padding: 0 }}>
                🗑️ Supprimer
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const tabItems = [
    {
      key: 'offers',
      label: (
        <span>
          <CheckCircleOutlined /> Offres ({offers.length})
        </span>
      ),
      children: (
        <div>
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total des offres"
                  value={offers.length}
                  prefix={<EyeOutlined />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Offres actives"
                  value={activeOffers.length}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Card
            title={
              <Space>
                <span>Liste des offres</span>
                {hasActiveOfferFilters && (
                  <Tag color="blue">{filteredOffers.length} / {offers.length} offre{filteredOffers.length > 1 ? 's' : ''}</Tag>
                )}
              </Space>
            }
            extra={
              !isAgent && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  Nouvelle offre
                </Button>
              )
            }
          >
            {/* Zone de recherche et filtres */}
            <div style={{ marginBottom: 16, padding: '16px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <Row gutter={[16, 12]} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <Input.Search
                    placeholder="Rechercher par titre, description..."
                    allowClear
                    value={offerSearchText}
                    onChange={(e) => setOfferSearchText(e.target.value)}
                    onSearch={(val) => setOfferSearchText(val)}
                    enterButton
                  />
                </Col>
                <Col xs={12} sm={6} md={5}>
                  <Select
                    placeholder="Mode"
                    style={{ width: '100%' }}
                    value={offerTypeFilter}
                    onChange={(val) => setOfferTypeFilter(val)}
                  >
                    <Select.Option value="all">Tous les modes</Select.Option>
                    <Select.Option value="on_demand">À la demande</Select.Option>
                    <Select.Option value="annual">Abonnement annuel</Select.Option>
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={5}>
                  <Select
                    placeholder="Catégorie"
                    style={{ width: '100%' }}
                    value={offerCategoryFilter}
                    onChange={(val) => setOfferCategoryFilter(val)}
                  >
                    <Select.Option value="all">Toutes les catégories</Select.Option>
                    <Select.Option value="only_all">Multi-équipements (Toutes catégories)</Select.Option>
                    {Object.entries(equipmentCategoryLabels)
                      .filter(([key]) => key !== 'all')
                      .map(([key, label]) => (
                        <Select.Option key={key} value={key}>{label}</Select.Option>
                      ))}
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <Select
                    placeholder="Statut"
                    style={{ width: '100%' }}
                    value={offerStatusFilter}
                    onChange={(val) => setOfferStatusFilter(val)}
                  >
                    <Select.Option value="all">Tous les statuts</Select.Option>
                    <Select.Option value="active">Actives uniquement</Select.Option>
                    <Select.Option value="inactive">Inactives uniquement</Select.Option>
                  </Select>
                </Col>
                {hasActiveOfferFilters && (
                  <Col xs={12} sm={6} md={2}>
                    <Button onClick={handleResetOfferFilters} icon={<ReloadOutlined />}>
                      Effacer
                    </Button>
                  </Col>
                )}
              </Row>
            </div>

            <Table
              columns={isAgent ? columns.filter(c => c.key !== 'actions') : columns}
              dataSource={filteredOffers}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'installation',
      label: (
        <span>
          🔧 Installation ({installationServices.length})
        </span>
      ),
      children: (
        <div>
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total des services"
                  value={installationServices.length}
                  prefix={<EyeOutlined />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Services actifs"
                  value={installationServices.filter(s => s.isActive).length}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Card
            title="Services d'Installation"
            extra={
              !isAgent && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateInstallation}>
                  Nouveau service
                </Button>
              )
            }
          >
            <Table
              columns={[
                {
                  title: 'Titre',
                  dataIndex: 'title',
                  key: 'title',
                  render: (text: string, record: InstallationService) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{text}</div>
                      {record.duration && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          {record.duration} mois
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  title: 'Modèle',
                  dataIndex: 'model',
                  key: 'model'
                },
                {
                  title: 'Prix',
                  dataIndex: 'price',
                  key: 'price',
                  render: (price: number | null) => price != null ? `${parseFloat(price.toString()).toLocaleString('fr-FR')} FCFA` : '-',
                  sorter: (a: InstallationService, b: InstallationService) => Number(a.price || 0) - Number(b.price || 0)
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  ellipsis: true
                },
                {
                  title: 'Statut',
                  dataIndex: 'isActive',
                  key: 'isActive',
                  render: (isActive: boolean) => (
                    <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                      {isActive ? 'Actif' : 'Inactif'}
                    </Tag>
                  ),
                  filters: [
                    { text: 'Actif', value: true },
                    { text: 'Inactif', value: false }
                  ],
                  onFilter: (value: any, record: InstallationService) => record.isActive === value
                },
                ...(!isAgent ? [{
                  title: 'Actions',
                  key: 'actions',
                  width: 200,
                  fixed: 'right' as const,
                  render: (_: any, record: InstallationService) => (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEditInstallation(record)}
                        size="small"
                        style={{ padding: 0 }}
                      >
                        Modifier
                      </Button>
                      <Popconfirm
                        title="Supprimer ce service ?"
                        onConfirm={() => handleDeleteInstallation(record.id)}
                        okText="Oui"
                        cancelText="Non"
                      >
                        <Button type="link" danger icon={<DeleteOutlined />} size="small" style={{ padding: 0 }}>
                          Supprimer
                        </Button>
                      </Popconfirm>
                      <Button
                        type="link"
                        onClick={() => handleToggleInstallation(record.id)}
                        size="small"
                        style={{ padding: 0 }}
                      >
                        {record.isActive ? 'Désactiver' : 'Activer'}
                      </Button>
                    </Space>
                  )
                }] : [])
              ]}
              dataSource={installationServices}
              rowKey="id"
              loading={loadingInstallation}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'reparation',
      label: (
        <span>
          🛠️ Réparation ({repairServices.length})
        </span>
      ),
      children: (
        <div>
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total des services"
                  value={repairServices.length}
                  prefix={<EyeOutlined />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Services actifs"
                  value={repairServices.filter(s => s.isActive).length}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Card
            title="Services de Réparation"
            extra={
              !isAgent && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateRepair} style={{ backgroundColor: '#ff9800', borderColor: '#ff9800' }}>
                  Nouveau service
                </Button>
              )
            }
          >
            <Table
              columns={[
                {
                  title: 'Titre',
                  dataIndex: 'title',
                  key: 'title',
                  render: (text: string, record: RepairService) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{text}</div>
                      {record.duration && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          {record.duration} mois
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  title: 'Modèle',
                  dataIndex: 'model',
                  key: 'model'
                },
                {
                  title: 'Prix',
                  dataIndex: 'price',
                  key: 'price',
                  render: (price: number | null) => price != null ? `${parseFloat(price.toString()).toLocaleString('fr-FR')} FCFA` : '-',
                  sorter: (a: RepairService, b: RepairService) => Number(a.price || 0) - Number(b.price || 0)
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  ellipsis: true
                },
                {
                  title: 'Statut',
                  dataIndex: 'isActive',
                  key: 'isActive',
                  render: (isActive: boolean) => (
                    <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                      {isActive ? 'Actif' : 'Inactif'}
                    </Tag>
                  ),
                  filters: [
                    { text: 'Actif', value: true },
                    { text: 'Inactif', value: false }
                  ],
                  onFilter: (value: any, record: RepairService) => record.isActive === value
                },
                ...(!isAgent ? [{
                  title: 'Actions',
                  key: 'actions',
                  width: 200,
                  fixed: 'right' as const,
                  render: (_: any, record: RepairService) => (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEditRepair(record)}
                        size="small"
                        style={{ padding: 0 }}
                      >
                        Modifier
                      </Button>
                      <Popconfirm
                        title="Supprimer ce service ?"
                        onConfirm={() => handleDeleteRepair(record.id)}
                        okText="Oui"
                        cancelText="Non"
                      >
                        <Button type="link" danger icon={<DeleteOutlined />} size="small" style={{ padding: 0 }}>
                          Supprimer
                        </Button>
                      </Popconfirm>
                      <Button
                        type="link"
                        onClick={() => handleToggleRepair(record.id)}
                        size="small"
                        style={{ padding: 0 }}
                      >
                        {record.isActive ? 'Désactiver' : 'Activer'}
                      </Button>
                    </Space>
                  )
                }] : [])
              ]}
              dataSource={repairServices}
              rowKey="id"
              loading={loadingRepair}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'subscriptions',
      label: (
        <span>
          <UserOutlined /> Souscriptions {pendingPayments > 0 && <Badge count={pendingPayments} />} ({subscriptions.length})
        </span>
      ),
      children: (
        <div>
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total souscriptions"
                  value={subscriptions.length}
                  prefix={<UserOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Souscriptions actives"
                  value={activeSubscriptions.length}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Revenus encaissés"
                  value={subscriptionRevenue.toLocaleString('fr-FR')}
                  suffix="FCFA"
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Contrats soldés + acomptes 50 % déjà versés
                </div>
              </Card>
            </Col>
          </Row>

          <Card
            title="Liste des souscriptions"
            extra={
              <Space>
                <Button icon={<CalendarOutlined />} onClick={loadSubscriptions}>
                  Actualiser
                </Button>
              </Space>
            }
          >
            <Table
              columns={isAgent ? subscriptionColumns.filter(c => c.key !== 'actions') : subscriptionColumns}
              dataSource={subscriptions}
              rowKey="id"
              loading={loadingSubscriptions}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="large"
        />
      </Card>

      <Modal
        title={editingOffer ? 'Modifier l\'offre' : 'Nouvelle offre'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Titre"
            rules={[{ required: true, message: 'Le titre est requis' }]}
          >
            <Input placeholder="Ex: Offre Premium" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'La description est requise' }]}
          >
            <TextArea rows={3} placeholder="Décrivez l'offre..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="offer_type"
                label="Mode de service"
                rules={[{ required: true, message: 'Sélectionnez un mode' }]}
              >
                <Select options={Object.entries(offerTypeLabels).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="equipment_category"
                label="Catégorie d'équipement"
                rules={[{ required: true, message: 'Sélectionnez une catégorie' }]}
              >
                <Select options={Object.entries(equipmentCategoryLabels).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="pricing_mode"
            label="Mode de tarification"
            rules={[{ required: true, message: 'Sélectionnez un mode de tarification' }]}
          >
            <Select options={Object.entries(pricingModeLabels).map(([value, label]) => ({ value, label }))} />
          </Form.Item>

          <Form.Item
            name="price"
            label="Prix (FCFA)"
            rules={[{ required: true, message: 'Le prix est requis' }]}
          >
            <InputNumber
              min={0}
              step={1000}
              style={{ width: '100%' }}
              placeholder="150000"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(value) => value!.replace(/\s?/g, '') as any}
            />
          </Form.Item>

          <Form.Item
            name="priceNote"
            label="Texte sous le prix (optionnel - affiché sur l'application mobile)"
          >
            <Input placeholder="Ex: / an, par équipement, TTC / an" />
          </Form.Item>

          {watchedOfferType === 'annual' && (
            <>
              <Divider orientation="left">Programmation annuelle</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="duration_months"
                    label="Durée (mois)"
                    rules={[{ required: true, message: 'Durée requise' }]}
                  >
                    <InputNumber min={1} max={36} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="visits_total"
                    label="Nombre de visites"
                    rules={[{ required: true, message: 'Nombre requis' }]}
                  >
                    <InputNumber min={1} max={24} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="visit_interval_months"
                    label="Intervalle (mois)"
                    rules={[{ required: true, message: 'Intervalle requis' }]}
                  >
                    <InputNumber min={1} max={12} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="annual_discount_percentage"
                label="Remise annuelle (%)"
                extra="La valeur recommandée est 5 %. Le serveur applique cette remise au montant de base du contrat."
                rules={[{ required: true, message: 'Remise requise' }]}
              >
                <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Aperçu tarifaire indicatif"
                description={`Pour 1 équipement : ${Number(watchedPrice).toLocaleString('fr-FR')} FCFA − ${watchedDiscount}% = ${Math.max(0, Number(watchedPrice) * (1 - Number(watchedDiscount) / 100)).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} FCFA. Le montant définitif est toujours calculé par le serveur lors de la souscription.`}
              />
            </>
          )}

          <Form.Item name="includes_freon" label="Fréon inclus" valuePropName="checked">
            <Switch checkedChildren="Inclus" unCheckedChildren="Non inclus" />
          </Form.Item>

          <Form.Item label="Fonctionnalités incluses">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onPressEnter={addFeature}
                  placeholder="Ex: 2 visites par an, Diagnostic offert ; Pièces incluses"
                />
                <Button type="primary" onClick={addFeature}>
                  Ajouter
                </Button>
              </Space.Compact>
              <div style={{ fontSize: '12px', color: '#888' }}>
                💡 <em>Vous pouvez ajouter plusieurs fonctionnalités d'un coup en les séparant par des virgules (,) ou des points-virgules (;).</em>
              </div>
              <div>
                {features.map((feature, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => removeFeature(index)}
                    style={{ marginBottom: '8px' }}
                  >
                    {feature}
                  </Tag>
                ))}
              </div>
            </Space>
          </Form.Item>

          {/* ====== OPTIONS PAYANTES ====== */}
          <Divider orientation="left">
            <span style={{ color: '#d97706', fontWeight: 600 }}>⚠️ Options payantes</span>
          </Divider>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12, fontSize: 12 }}
            message="Ces options ne sont pas incluses dans l'abonnement de base. Elles sont facturées séparément au client selon ses besoins."
          />

          {/* Options prédéfinies du catalogue */}
          <div style={{ marginBottom: 12 }}>
            {([
              { label: 'Diagnostic préalable', price: 4000, note: 'FCFA (fixe)' },
              { label: 'Appoint / Rechargement de gaz (fréon)', price: null, note: 'Sur devis' },
              { label: 'Réparation après devis validé', price: null, note: 'Sur devis' },
              { label: 'Devis grandes pièces (compresseur, moteur…)', price: null, note: 'Sur devis' },
              { label: 'Offre bimestrielle disponible (6 passages/an)', price: null, note: 'Prix variable' },
              { label: 'Déplacement hors Abidjan', price: null, note: '3 000 – 5 000 FCFA' },
            ] as PaidOption[]).map((preset) => {
              const isChecked = paidOptions.some(o => o.label === preset.label);
              return (
                <div
                  key={preset.label}
                  onClick={() => {
                    if (isChecked) {
                      setPaidOptions(prev => prev.filter(o => o.label !== preset.label));
                    } else {
                      setPaidOptions(prev => [...prev, preset]);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    marginBottom: 6,
                    borderRadius: 8,
                    cursor: 'pointer',
                    border: `1px solid ${isChecked ? '#f59e0b' : '#e5e7eb'}`,
                    backgroundColor: isChecked ? '#fffbeb' : '#fafafa',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{
                    width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${isChecked ? '#f59e0b' : '#d1d5db'}`,
                    backgroundColor: isChecked ? '#f59e0b' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {isChecked && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>}
                  </span>
                  <span style={{ flex: 1, fontSize: 13 }}>{preset.label}</span>
                  <Tag color={preset.price !== null ? 'orange' : 'default'} style={{ fontSize: 11 }}>
                    {preset.price !== null ? `${preset.price.toLocaleString('fr-FR')} FCFA` : preset.note}
                  </Tag>
                </div>
              );
            })}
          </div>

          {/* Option personnalisée */}
          <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
            <Input
              placeholder="Ajouter une option payante personnalisée..."
              value={customPaidOption}
              onChange={e => setCustomPaidOption(e.target.value)}
              onPressEnter={() => {
                if (customPaidOption.trim()) {
                  setPaidOptions(prev => [...prev, { label: customPaidOption.trim(), price: null, note: 'Sur devis', isCustom: true }]);
                  setCustomPaidOption('');
                }
              }}
            />
            <Button
              onClick={() => {
                if (customPaidOption.trim()) {
                  setPaidOptions(prev => [...prev, { label: customPaidOption.trim(), price: null, note: 'Sur devis', isCustom: true }]);
                  setCustomPaidOption('');
                }
              }}
            >
              + Ajouter
            </Button>
          </Space.Compact>

          {/* Récapitulatif des options sélectionnées */}
          {paidOptions.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Options sélectionnées :</div>
              {paidOptions.map((opt, idx) => (
                <Tag
                  key={idx}
                  closable
                  color="orange"
                  onClose={() => setPaidOptions(prev => prev.filter((_, i) => i !== idx))}
                  style={{ marginBottom: 6 }}
                >
                  {opt.label}{opt.price !== null ? ` — ${opt.price.toLocaleString('fr-FR')} FCFA` : ''}
                </Tag>
              ))}
            </div>
          )}

          <Form.Item name="isActive" label="Statut" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Installation Services */}
      <Modal
        title={editingInstallation ? 'Modifier le service' : 'Nouveau service d\'installation'}
        open={installationModalVisible}
        onOk={handleSubmitInstallation}
        onCancel={() => setInstallationModalVisible(false)}
        width={600}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={installationForm} layout="vertical">
          <Form.Item
            name="title"
            label="Titre du service"
            rules={[{ required: true, message: 'Le titre est requis' }]}
          >
            <Input placeholder="Ex: Climatiseurs Split" />
          </Form.Item>

          <Form.Item
            name="model"
            label="Modèle/Type"
          >
            <Input placeholder="Ex: CARRIER, LK, TOSHIBA ..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (optionnelle)"
          >
            <TextArea rows={3} placeholder="Description détaillée du service..." />
          </Form.Item>

          <Form.Item
            name="availabilityInfo"
            label="Disponibilité"
            initialValue="Tous les jours et week-ends jusqu'à 17h"
          >
            <Input placeholder="Ex: Tous les jours et week-ends jusqu'à 17h" />
          </Form.Item>

          <Form.Item name="isActive" label="Statut" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Repair Services */}
      <Modal
        title={editingRepair ? 'Modifier le service' : 'Nouveau service de réparation'}
        open={repairModalVisible}
        onOk={handleSubmitRepair}
        onCancel={() => setRepairModalVisible(false)}
        width={600}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={repairForm} layout="vertical">
          <Form.Item
            name="title"
            label="Titre du service"
            rules={[{ required: true, message: 'Le titre est requis' }]}
          >
            <Input placeholder="Ex: Fuite de Gaz" />
          </Form.Item>

          <Form.Item
            name="model"
            label="Modèle/Type"
          >
            <Input placeholder="Ex: R410A, R32, R22" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (optionnelle)"
          >
            <TextArea rows={3} placeholder="Description détaillée du service..." />
          </Form.Item>

          <Form.Item name="isActive" label="Statut" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MaintenanceOffersPage;
